import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import MainDrawer from "../MainDrawer";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";
import APITable from "./APITable";
import MiniBot from "../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const APIPage: React.FC = () => {
    const [apis, setAPIs] = useState([]);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);

    const [addApiMenuAnchorEl, setAddApiMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isAddApiMenuOpen = Boolean(addApiMenuAnchorEl);
    const [isLoading, setIsLoading] = useState(true);
    const [menuStep, setMenuStep] = useState(1); 


    

    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>({
        name: true,
        applicationName: true,
        serviceName: true,
        cloudProvider: true,
        solutionOwner: true,
        createdAt: true,
    });
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const toggleColumnFilter = (column: string) => {
        setColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleAddApiMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      setAddApiMenuAnchorEl(event.currentTarget);
   };

   const handleAddApiMenuClose = () => {
       setAddApiMenuAnchorEl(null);
       setMenuStep(1); // <-- ADD THIS LINE to reset the menu
   };

   const handleAddApiMenuItemClick = (serviceType: string) => {
       navigate(`/apis/add/${serviceType}`);
       handleAddApiMenuClose();
   };

   // ADD THIS NEW HANDLER
   const handleExistingApiClick = () => {
       navigate(`/apis/add/existing`); 
       handleAddApiMenuClose();
   };

    React.useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true); 
            try {
                const data = await queryGraphql(`
                    query GetAPIBackends {
                      getAPIBackends {
                        id
                        name
                        cloudProvider
                        environment
                        status
                        apiBackendOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        apiBackendApprover {
                          id
                          name
                          googleProfilePicture
                        }
                        application {
                          applicationName
                          id
                        }
                        service {
                          id
                          serviceName
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        costLabelPairs {
                          cost
                          label
                        }
                        codeTemplate
                        path
                        httpMethod
                        apiVersion
                        description
                        mockRequest
                        mockResponse
                        serviceType
                      }
                    }
                `);

                let apisArr = data.getAPIBackends.map((api: any) => ({
                    ...api,
                    solutionOwner: api?.apiBackendOwner?.name,
                    applicationName: api?.application?.applicationName,
                    serviceName: api?.service?.serviceName,
                    createdAt: new Date(Number(api.createdAt)).toISOString(),
                    googleProfilePicture: api?.apiBackendOwner?.googleProfilePicture,
                }));

                setAPIs(apisArr);

                searchOptions = apisArr
                    .map((api: any) => [
                        {
                            category: "name",
                            groupBy: "API NAME",
                            chipLabel: `API Name: ${api.name}`,
                            dropdownLabel: api.name,
                        },
                        {
                            category: "cloudProvider",
                            groupBy: "CLOUD PROVIDER",
                            chipLabel: `Provider: ${api.cloudProvider}`,
                            dropdownLabel: api.cloudProvider,
                        },
                        {
                            category: "environment",
                            groupBy: "ENVIRONMENT",
                            chipLabel: `Environment: ${api.environment}`,
                            dropdownLabel: api.environment,
                        },
                        {
                            category: "applicationName",
                            groupBy: "APPLICATION NAME",
                            chipLabel: `Application: ${api.applicationName}`,
                            dropdownLabel: api.applicationName,
                        },
                         {
                            category: "serviceName",
                            groupBy: "SERVICE NAME",
                            chipLabel: `Service: ${api.serviceName}`,
                            dropdownLabel: api.serviceName,
                        },
                        {
                            category: "solutionOwner",
                            groupBy: "OWNER",
                            chipLabel: `Owner: ${api.solutionOwner}`,
                            dropdownLabel: api.solutionOwner,
                        },
                    ])
                    .flat();

                // Remove duplicates and sort
                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });
            } catch (error) {
                console.log(error);
            }finally {
            setIsLoading(false); 
        }
        };

        fetchData();
    }, []);

    const filteredAPIs =
        searchQuery.length === 0
            ? apis
            : apis.filter((api: any) =>
                Object.entries({
                    name: api.name,
                    cloudProvider: api.cloudProvider,
                    environment: api.environment,
                    applicationName: api.applicationName,
                    serviceName: api.serviceName,
                    solutionOwner: api.solutionOwner,
                }).every(([category, value]) => {
                    const filters = searchQuery.filter((e) => e.category === category);
                    if (filters.length === 0) return true;
                    return filters.map((e) => e.dropdownLabel.toLowerCase()).includes(value.toLowerCase());
                })
            );

    const totalAPIsOverall = apis.length;
    const totalMatchingSearch = filteredAPIs.length;
    const filteredActiveAPIs = filteredAPIs.filter((api: any) => api.status === "active");
    const filteredPendingAPIs = filteredAPIs.filter((api: any) => api.status === "pending");
    const filteredRejectedAPIs = filteredAPIs.filter((api: any) => api.status === "rejected");

    return (
        <Box sx={{ display: "flex" }}>
            <MiniBot />
            <MainDrawer index={"apis-backend"} />
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: window.innerHeight }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 1,
                                mx: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                APIs Backend 
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                {/* <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!user.roles?.some((e: string) => ["developer", "viewer"].includes(e))}
                                    sx={{
                                        backgroundColor: "black",
                                        color: "white",
                                        padding: "8px 24px",
                                        fontSize: "0.875rem",
                                        textTransform: "none",
                                    }}
                                    onClick={() => navigate("/apis/add")}
                                >
                                    <Typography fontSize="14px" fontFamily="Gilroy-Bold">
                                        Add New API
                                    </Typography>
                                </Button> */}

                                <Button
    id="add-api-button"
    aria-controls={isAddApiMenuOpen ? 'add-api-menu' : undefined}
    aria-haspopup="true"
    aria-expanded={isAddApiMenuOpen ? 'true' : undefined}
    variant="contained"
    disabled={!user.roles?.some((e: string) => ["developer", "viewer"].includes(e))}
    sx={{
        backgroundColor: "black",
        color: "white",
        padding: "8px 24px",
        fontSize: "0.875rem",
        textTransform: "none",
        '&:hover': {
            backgroundColor: '#333',
        }
    }}
    onClick={handleAddApiMenuClick}
>
    <Typography fontSize="14px" fontFamily="Gilroy-Bold">
        Add New API Backend
    </Typography>
</Button>
<Menu
    id="add-api-menu"
    MenuListProps={{
        'aria-labelledby': 'add-api-button',
    }}
    anchorEl={addApiMenuAnchorEl}
    open={isAddApiMenuOpen}
    onClose={handleAddApiMenuClose}
    anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
    }}
    transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
    }}
>
    {/* Step 1: Choose New or Existing */}
    {menuStep === 1 && (
        <div>
            <MenuItem onClick={() => setMenuStep(2)}>
                New API Backend
            </MenuItem>
            <MenuItem onClick={handleExistingApiClick}>
                Existing API Backend
            </MenuItem>
        </div>
    )}

    {/* Step 2: Choose Service Type for New API */}
    {menuStep === 2 && (
        <div>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => handleAddApiMenuItemClick('kubernetes')}>
                Kubernetes API
            </MenuItem>
            <MenuItem onClick={() => handleAddApiMenuItemClick('serverless')}>
                Serverless API
            </MenuItem>
        </div>
    )}
</Menu>
                                <IconButton
                                    aria-label="Help"
                                    onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                                    sx={{ padding: 0, border: "none", ml: 2, "&:hover": { backgroundColor: "action.hover" } }}
                                >
                                    <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40, height: 40, display: "block" }} />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by API name, environment, owner etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px", paddingLeft: "4px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                            <Button
                                sx={{ marginLeft: "16px", border: "1px solid #bbb", borderRadius: "10px", height: "45px", width: "45px" }}
                                onClick={handleFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            >
                                <Box sx={{ padding: "8px" }}>
                                    <Typography sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.keys(columnFilters).map((column) => (
                                            <FormControlLabel
                                                key={column}
                                                control={
                                                    <Checkbox
                                                        checked={columnFilters[column as keyof typeof columnFilters]}
                                                        onChange={() => toggleColumnFilter(column)}
                                                        sx={{ color: "#000", "&.Mui-checked": { color: "#000" }, "& .MuiSvgIcon-root": { borderRadius: "50%" } }}
                                                    />
                                                }
                                                label={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, " $1").trim()}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button variant="contained" onClick={handleFilterClose} sx={{ marginTop: "8px", backgroundColor: "black", color: "white", width: "100%" }}>
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>
                    <Box m={2}>
                        {totalAPIsOverall > 0 && totalMatchingSearch > 0 && (
                            <>
                        {filteredPendingAPIs.length > 0 && (
                            <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                            Pending APIs Backend({filteredPendingAPIs.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <APITable apis={filteredPendingAPIs} columnFilters={columnFilters} type="pending" />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {filteredActiveAPIs.length > 0 && (
                            <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                            Active APIs Backend({filteredActiveAPIs.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <APITable apis={filteredActiveAPIs} columnFilters={columnFilters} type="active" />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {filteredRejectedAPIs.length > 0 && (
                            <Accordion elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                            Rejected APIs Backend({filteredRejectedAPIs.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <APITable apis={filteredRejectedAPIs} columnFilters={columnFilters} type="rejected" />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        </>)}

                                {totalAPIsOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No APIs Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No APIs match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {/* Case 3: No APIs have been added to the system at all */}
                        {totalAPIsOverall === 0 && !isLoading && (
                            <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No APIs" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No APIs are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first API.
                                </Typography>
                            </Box>
                        )}
                        
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default APIPage;